import React, { createContext, useState, useEffect, useContext } from "react";

const LOCAL_STORAGE_KEY = "tvz_currency";
const FORCE_CURRENCY_KEY = "tvz_force_currency";
const LEGACY_CODE_KEY = "tvz_currency_code";
const LEGACY_SYMBOL_KEY = "tvz_currency_symbol";

const OLD_LOCAL_STORAGE_KEY = "ticketverz_currency";
const OLD_FORCE_CURRENCY_KEY = "ticketverz_force_currency";
const OLD_LEGACY_CODE_KEY = "currency_code";
const OLD_LEGACY_SYMBOL_KEY = "currency_symbol";

const DEFAULT_CURRENCY = { code: "EUR", symbol: "€" };

const CurrencyContext = createContext();

function readMigratedCurrency() {
  try {
    const raw = localStorage.getItem(LOCAL_STORAGE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw);
      if (parsed?.code && parsed?.symbol) {
        return parsed;
      }
    }

    const oldRaw = localStorage.getItem(OLD_LOCAL_STORAGE_KEY);
    if (oldRaw) {
      const parsed = JSON.parse(oldRaw);
      if (parsed?.code && parsed?.symbol) {
        localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(parsed));
        localStorage.setItem(LEGACY_CODE_KEY, parsed.code);
        localStorage.setItem(LEGACY_SYMBOL_KEY, parsed.symbol);
        return parsed;
      }
    }

    const newCode = localStorage.getItem(LEGACY_CODE_KEY);
    const newSymbol = localStorage.getItem(LEGACY_SYMBOL_KEY);
    if (newCode && newSymbol) {
      const migrated = { code: newCode, symbol: newSymbol };
      localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(migrated));
      return migrated;
    }

    const oldCode = localStorage.getItem(OLD_LEGACY_CODE_KEY);
    const oldSymbol = localStorage.getItem(OLD_LEGACY_SYMBOL_KEY);
    if (oldCode && oldSymbol) {
      const migrated = { code: oldCode, symbol: oldSymbol };
      localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(migrated));
      localStorage.setItem(LEGACY_CODE_KEY, oldCode);
      localStorage.setItem(LEGACY_SYMBOL_KEY, oldSymbol);
      return migrated;
    }
  } catch {}

  return null;
}

function hasExistingCurrencyState() {
  try {
    const forced =
      localStorage.getItem(FORCE_CURRENCY_KEY) ||
      localStorage.getItem(OLD_FORCE_CURRENCY_KEY);

    const raw =
      localStorage.getItem(LOCAL_STORAGE_KEY) ||
      localStorage.getItem(OLD_LOCAL_STORAGE_KEY);

    const code =
      localStorage.getItem(LEGACY_CODE_KEY) ||
      localStorage.getItem(OLD_LEGACY_CODE_KEY);

    const symbol =
      localStorage.getItem(LEGACY_SYMBOL_KEY) ||
      localStorage.getItem(OLD_LEGACY_SYMBOL_KEY);

    return Boolean(forced || raw || (code && symbol));
  } catch {
    return false;
  }
}

export const CurrencyProvider = ({ children }) => {
  const [currency, setCurrency] = useState(() => readMigratedCurrency());

  const [isDetecting, setIsDetecting] = useState(
    () => !hasExistingCurrencyState(),
  );

  useEffect(() => {
    if (!currency?.code || !currency?.symbol) return;

    try {
      localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(currency));
      localStorage.setItem(LEGACY_CODE_KEY, currency.code);
      localStorage.setItem(LEGACY_SYMBOL_KEY, currency.symbol);
    } catch {}
  }, [currency]);

  const setCurrencyDetected = (next) => {
    if (!next?.code || !next?.symbol) {
      setCurrency(DEFAULT_CURRENCY);
      setIsDetecting(false);
      return;
    }

    setCurrency(next);
    setIsDetecting(false);
  };

  const setCurrencyManually = (next) => {
    if (!next?.code || !next?.symbol) return;

    try {
      localStorage.setItem(FORCE_CURRENCY_KEY, "1");
    } catch {}

    setCurrency(next);
    setIsDetecting(false);
  };

  return (
    <CurrencyContext.Provider
      value={{
        currency: currency || DEFAULT_CURRENCY,
        setCurrency,
        setCurrencyDetected,
        setCurrencyManually,
        isDetecting,
        setIsDetecting,
      }}
    >
      {children}
    </CurrencyContext.Provider>
  );
};

export const useCurrency = () => useContext(CurrencyContext);
